import React from "react";
import PropTypes from "prop-types";

// Helper function to apply translate and opacity with CSS transition
function translateXY(el, x, y, duration = 0, opacity = undefined) {
    if (!el) return;

    const transitionProps = 'transform, opacity';
    el.style.transition = `${transitionProps} ${duration}ms ease-out`;
    el.style.webkitTransition = `-webkit-transform ${duration}ms ease-out, opacity ${duration}ms ease-out`;

    el.style.transform = `translate(${x}px, ${y}px)`;
    el.style.webkitTransform = `translate(${x}px, ${y}px) translateZ(0)`; // Force hardware acceleration

    if (typeof opacity === 'number') {
        el.style.opacity = opacity;
    }
}


const styles = {
    wrapper: {
        width: "100%",
        height: "100%", // Dynamically overridden by JS
        position: "relative",
        overflow: "hidden" // Prevents sibling frames from causing horizontal/vertical overflow
    },
    frame: {
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        backgroundColor: "#ffffff",
        // Opacity controlled by JS
    },
    innerFrameWrapper: {
        display: 'block', // For height calculation
        height: 'auto',   // Natural height based on content
        width: '100%'
    }
};

class SVCarousel extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            frames: [].concat(props.frames || props.children || []),
            currentFrameIndex: props.onStartFrameIndex || 0,
            frameWidth: 0,
            frameHeight: 0,
            movingFrames: null, // Refs to current, prev, next frame elements
            slider: null, // Timeout ID for auto-sliding
            startX: 0, startY: 0, deltaX: 0, deltaY: 0, // Drag state
        };

        this.mounted = false;
        this.debounceTimeoutId = null;
        this.removeHeightTransitionTimeoutId = null;
        this.dragStartHeights = null; // Cache frame heights at drag start
        this.resizeObserver = null;

        // Bind methods
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.onResize = this.onResize.bind(this);
        this.autoSlide = this.autoSlide.bind(this);
        this.prev = this.prev.bind(this);
        this.next = this.next.bind(this);
        this.changeFrame = this.changeFrame.bind(this);
        this.setFrame = this.setFrame.bind(this);
        this.getFrameContentHeight = this.getFrameContentHeight.bind(this);
        this.updateWrapperHeight = this.updateWrapperHeight.bind(this);
        this.observeFrames = this.observeFrames.bind(this);
        this.prepareSiblingFrames = this.prepareSiblingFrames.bind(this);
        this.transitFramesTowards = this.transitFramesTowards.bind(this);
        this.getFrameId = this.getFrameId.bind(this);
        this.getSiblingFrames = this.getSiblingFrames.bind(this);
        this.getSiblingFramesByOverride = this.getSiblingFramesByOverride.bind(this);
        this.decideEndPosition = this.decideEndPosition.bind(this);
        this.moveFramesBy = this.moveFramesBy.bind(this);
        this.clearAutoTimeout = this.clearAutoTimeout.bind(this);
        this.updateFrameSize = this.updateFrameSize.bind(this);
        this.prepareAutoSlide = this.prepareAutoSlide.bind(this);
        this.transitToIndex = this.transitToIndex.bind(this);

        if (props.loop === false && props.auto) {
            console.warn("[SVCarousel] Auto-slide only works reliably in loop mode.");
        }
    }

    // --- Lifecycle Methods ---

    componentDidMount() {
        this.mounted = true;
        this.updateFrameSize(() => {
            this.prepareSiblingFrames(true, this.state.currentFrameIndex); // Initial positioning

            // Set up ResizeObserver to handle asynchronous content loading
            if (typeof window !== 'undefined' && window.ResizeObserver) {
                this.resizeObserver = new window.ResizeObserver(() => {
                    if (this.mounted) {
                        this.updateWrapperHeight(this.state.currentFrameIndex, 0);
                    }
                });
                this.observeFrames();
            }

            // Delay height update to ensure content is fully rendered and measurable
            requestAnimationFrame(() => {
                if (this.mounted) {
                    this.updateWrapperHeight(this.state.currentFrameIndex, 0); // Initial height
                }
            });
        });

        const wrapperEl = this.refs.wrapper;
        if (wrapperEl) {
            wrapperEl.addEventListener("touchstart", this.onTouchStart, {passive: true});
            wrapperEl.addEventListener("touchmove", this.onTouchMove, {capture: true});
            wrapperEl.addEventListener("touchend", this.onTouchEnd, {capture: true});
            wrapperEl.addEventListener("mousedown", this.onTouchStart, {capture: true});
            // mousemove/up/leave added dynamically
        } else {
            console.error("[SVCarousel] Wrapper ref not found on mount.");
        }

        window.addEventListener("resize", this.onResize);

        if (this.props.currentFrameIndex !== undefined && this.props.currentFrameIndex !== this.state.currentFrameIndex) {
            this.setFrame(this.props.currentFrameIndex);
        } else {
            this.prepareAutoSlide();
        }

        if (this.props.setClick) {
            this.props.setClick(this.changeFrame);
        }
    }

    componentWillUnmount() {
        this.mounted = false;
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        this.clearAutoTimeout();
        clearTimeout(this.debounceTimeoutId);
        clearTimeout(this.removeHeightTransitionTimeoutId);

        const wrapperEl = this.refs.wrapper;
        if (wrapperEl) {
            wrapperEl.removeEventListener("touchstart", this.onTouchStart);
            wrapperEl.removeEventListener("touchmove", this.onTouchMove, {capture: true});
            wrapperEl.removeEventListener("touchend", this.onTouchEnd, {capture: true});
            wrapperEl.removeEventListener("mousedown", this.onTouchStart, {capture: true});
            wrapperEl.removeEventListener("mousemove", this.onTouchMove, {capture: true});
            wrapperEl.removeEventListener("mouseup", this.onTouchEnd, {capture: true});
            wrapperEl.removeEventListener("mouseleave", this.onTouchEnd, {capture: true});
        }
        window.removeEventListener("resize", this.onResize);
    }

    componentDidUpdate(prevProps, prevState) {
        // Check if frame content actually changed (simple serialization check)
        const newFramesContent = JSON.stringify(this.state.frames.map(f => f.key || f.props));
        const oldFramesContent = JSON.stringify(prevState.frames.map(f => f.key || f.props));

        if (this.state.frames.length !== prevState.frames.length || newFramesContent !== oldFramesContent) {
            // Frames changed, re-initialize layout
            this.updateFrameSize(() => {
                this.prepareSiblingFrames(true, 0); // Reset to frame 0
                this.observeFrames(); // Re-attach observer to new elements
                this.updateWrapperHeight(0, 0);
                this.prepareAutoSlide();
            });
        }

        // Handle external control via desiredSlide prop
        if (this.props.desiredSlide !== undefined &&
            this.props.desiredSlide !== prevProps.desiredSlide &&
            this.props.desiredSlide !== this.state.currentFrameIndex) {
            this.changeFrame();
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const nextFrames = [].concat(nextProps.frames || nextProps.children || []);
        const newState = {};

        // Check if frames array content or length actually changed
        const framesChanged = (
            nextFrames.length !== prevState.frames.length ||
            JSON.stringify(nextFrames.map(f => f.key || f.props)) !== JSON.stringify(prevState.frames.map(f => f.key || f.props))
        );

        if (framesChanged) {
            newState.frames = nextFrames;
            newState.currentFrameIndex = 0; // Reset index if frames changed
        } else if (prevState.frames !== nextFrames) {
            // If only reference changed, update state.frames to keep it consistent
            newState.frames = nextFrames;
        }

        return Object.keys(newState).length > 0 ? newState : null;
    }

    // --- Frame Content Height Calculation ---

    getFrameContentHeight(frameEl) {
        if (!frameEl) return 0;
        const contentWrapper = frameEl.querySelector('[data-frame-content-wrapper="true"]');
        return contentWrapper ? contentWrapper.offsetHeight : 0;
    }

    // Updates the main wrapper's height to match the target frame's content
    updateWrapperHeight(frameIndex = this.state.currentFrameIndex, duration = 0) {
        if (!this.mounted || !this.refs.wrapper || frameIndex < 0 || frameIndex >= this.state.frames.length) return;

        const targetFrameEl = this.refs[`f${frameIndex}`];
        const wrapperEl = this.refs.wrapper;

        if (targetFrameEl && wrapperEl) {
            const newHeight = this.getFrameContentHeight(targetFrameEl);
            if (newHeight > 0) {
                // Update state if height changed, ensures React doesn't overwrite manual DOM change
                if (newHeight !== this.state.frameHeight) {
                    this.setState({frameHeight: newHeight});
                }

                clearTimeout(this.removeHeightTransitionTimeoutId);
                this.removeHeightTransitionTimeoutId = null;

                // Apply CSS transition *before* setting the new height
                wrapperEl.style.transition = duration > 0 ? `height ${duration}ms ease-out` : '';
                wrapperEl.style.height = `${newHeight}px`;

                // Schedule transition removal *after* it completes
                if (duration > 0) {
                    this.removeHeightTransitionTimeoutId = setTimeout(() => {
                        if (this.refs.wrapper) this.refs.wrapper.style.transition = '';
                        this.removeHeightTransitionTimeoutId = null;
                    }, duration);
                }
            }
        }
    }

    // Attach ResizeObserver to all frames' content wrappers
    observeFrames() {
        if (!this.resizeObserver || !this.mounted) return;
        this.resizeObserver.disconnect();
        this.state.frames.forEach((_, i) => {
            const frameEl = this.refs[`f${i}`];
            if (frameEl) {
                const contentWrapper = frameEl.querySelector('[data-frame-content-wrapper="true"]');
                if (contentWrapper) {
                    this.resizeObserver.observe(contentWrapper);
                }
            }
        });
    }

    // --- Frame Positioning and Preparation ---

    // Calculates the indices for current, previous, and next frames
    getFrameId(pos, current = this.state.currentFrameIndex) {
        const {frames} = this.state;
        const total = frames.length;
        if (total === 0) return 0;

        let targetIndex;
        switch (pos) {
            case "prev":
                targetIndex = current - 1;
                break;
            case "next":
                targetIndex = current + 1;
                break;
            default:
                targetIndex = current;
                break;
        }

        return this.props.loop ? (targetIndex + total) % total : Math.max(0, Math.min(total - 1, targetIndex));
    }

    // Retrieves the DOM elements for current, previous, and next frames
    getSiblingFrames() {
        const {frames, currentFrameIndex} = this.state;
        if (frames.length === 0) return {currentFrameIndex: null, prev: null, next: null};
        const currentId = this.getFrameId("current", currentFrameIndex);
        const prevId = this.getFrameId("prev", currentId);
        const nextId = this.getFrameId("next", currentId);
        const siblings = {
            currentFrameIndex: this.refs["f" + currentId],
            prev: this.refs["f" + prevId],
            next: this.refs["f" + nextId]
        };
        if (!this.props.loop) {
            if (currentId === 0) siblings.prev = null;
            if (currentId === frames.length - 1) siblings.next = null;
        }
        if (siblings.prev === siblings.currentFrameIndex) siblings.prev = null;
        if (siblings.next === siblings.currentFrameIndex) siblings.next = null;
        return siblings;
    }

    // Retrieves sibling frames based on a specific index override
    getSiblingFramesByOverride(index) {
        const {frames} = this.state;
        if (frames.length === 0 || index === undefined || index < 0 || index >= frames.length) return {
            currentFrameIndex: null,
            prev: null,
            next: null
        };
        const currentId = index;
        const prevId = this.getFrameId("prev", currentId);
        const nextId = this.getFrameId("next", currentId);
        const siblings = {
            currentFrameIndex: this.refs["f" + currentId],
            prev: this.refs["f" + prevId],
            next: this.refs["f" + nextId]
        };
        if (!this.props.loop) {
            if (index === 0) siblings.prev = null;
            if (index === frames.length - 1) siblings.next = null;
        }
        if (siblings.prev === siblings.currentFrameIndex) siblings.prev = null;
        if (siblings.next === siblings.currentFrameIndex) siblings.next = null;
        return siblings;
    }


    // Positions current, previous, and next frames correctly. Sets initial opacity.
    prepareSiblingFrames(override = false, index = undefined) {
        let indexToUse = override ? index : this.state.currentFrameIndex;
        if (indexToUse === undefined || indexToUse < 0 || indexToUse >= this.state.frames.length) {
            if (this.state.frames.length > 0) indexToUse = 0; else {
                this.setState({movingFrames: null});
                return;
            }
        }

        const siblings = override ? this.getSiblingFramesByOverride(indexToUse) : this.getSiblingFrames();
        const {currentFrameIndex: currentEl, prev: prevEl, next: nextEl} = siblings;
        const {frameWidth, frameHeight} = this.state;

        if (!currentEl) {
            if (this.state.movingFrames) this.setState({movingFrames: null});
            return siblings;
        }

        // Position current frame (visible) and siblings (off-screen, hidden) instantly
        translateXY(currentEl, 0, 0, 0, 1);
        if (this.props.axis === "x") {
            translateXY(prevEl, -frameWidth, 0, 0, 0);
            translateXY(nextEl, frameWidth, 0, 0, 0);
        } else if (frameHeight) { // Only position vertically if height is known
            translateXY(prevEl, 0, -frameHeight, 0, 0);
            translateXY(nextEl, 0, frameHeight, 0, 0);
        } else { // Hide siblings at origin if height unknown
            translateXY(prevEl, 0, 0, 0, 0);
            translateXY(nextEl, 0, 0, 0, 0);
        }

        // Explicitly hide all other frames instantly
        this.state.frames.forEach((_, i) => {
            const frameEl = this.refs[`f${i}`];
            if (frameEl && frameEl !== currentEl && frameEl !== prevEl && frameEl !== nextEl) {
                translateXY(frameEl, 0, 0, 0, 0);
            }
        });

        // Update movingFrames state only if refs changed
        if (!this.state.movingFrames || this.state.movingFrames.currentFrameIndex !== currentEl || this.state.movingFrames.prev !== prevEl || this.state.movingFrames.next !== nextEl) {
            this.setState({movingFrames: siblings});
        }
        return siblings;
    }

    // --- Touch and Mouse Event Handlers ---

    onTouchStart(e) {
        if (this.state.frames.length < 2 || (e.type === 'mousedown' && e.button !== 0)) return;
        this.clearAutoTimeout();
        this.prepareSiblingFrames(); // Ensure frames are positioned & movingFrames state is set

        if (!this.state.movingFrames || !this.state.movingFrames.currentFrameIndex) return; // Abort if prepare failed

        // Clear any ongoing CSS height transition
        const wrapperEl = this.refs.wrapper;
        if (wrapperEl) wrapperEl.style.transition = '';
        clearTimeout(this.removeHeightTransitionTimeoutId);
        this.removeHeightTransitionTimeoutId = null;

        // Cache frame heights for interpolation
        const {currentFrameIndex: currentEl, prev: prevEl, next: nextEl} = this.state.movingFrames;
        this.dragStartHeights = {
            current: this.getFrameContentHeight(currentEl),
            prev: this.getFrameContentHeight(prevEl),
            next: this.getFrameContentHeight(nextEl)
        };
        if (this.dragStartHeights.current === 0 && currentEl) { // Fallback if height calculation failed initially
            this.updateWrapperHeight(this.state.currentFrameIndex, 0);
            this.dragStartHeights.current = this.getFrameContentHeight(currentEl);
        }

        // Record start position
        const touch = (e.touches && e.touches[0]) || e;
        this.setState({startX: touch.pageX, startY: touch.pageY, deltaX: 0, deltaY: 0});

        // Add mouse listeners dynamically
        if (wrapperEl && e.type === 'mousedown') {
            wrapperEl.addEventListener("mousemove", this.onTouchMove, {capture: true});
            wrapperEl.addEventListener("mouseup", this.onTouchEnd, {capture: true});
            wrapperEl.addEventListener("mouseleave", this.onTouchEnd, {capture: true});
        }
    }

    onTouchMove(e) {
        if ((e.type === 'mousemove' && e.buttons !== 1)) {
            this.onTouchEnd(e);
            return;
        } // End drag if mouse button released
        if (e.touches && e.touches.length > 1) return; // Ignore multi-touch
        if (this.dragStartHeights === null) return; // Ignore if drag didn't start properly

        this.clearAutoTimeout();
        const touch = (e.touches && e.touches[0]) || e;
        let deltaX = touch.pageX - this.state.startX;
        let deltaY = touch.pageY - this.state.startY;

        // Apply drag resistance at boundaries if not looping
        if (!this.props.loop) {
            const isFirst = this.state.currentFrameIndex === 0;
            const isLast = this.state.currentFrameIndex === this.state.frames.length - 1;
            if (this.props.axis === 'x') {
                if ((isFirst && deltaX > 0) || (isLast && deltaX < 0)) deltaX /= 3;
            } else {
                if ((isFirst && deltaY > 0) || (isLast && deltaY < 0)) deltaY /= 3;
            }
        }

        this.setState({deltaX: deltaX, deltaY: deltaY});

        // --- Dynamic Height Interpolation during Drag ---
        const wrapperEl = this.refs.wrapper;
        if (wrapperEl && this.dragStartHeights) {
            const {current: currentHeight, prev: prevHeight, next: nextHeight} = this.dragStartHeights;
            const {frameWidth} = this.state;
            const effectiveFrameHeight = this.state.frameHeight || currentHeight || 1;
            let progress = 0, startHeight = currentHeight, endHeight = currentHeight;

            if (this.props.axis === 'x' && frameWidth > 0) {
                progress = Math.abs(deltaX) / frameWidth;
                endHeight = deltaX < 0 ? (nextHeight || currentHeight) : (prevHeight || currentHeight);
            } else if (this.props.axis === 'y' && effectiveFrameHeight > 0) {
                progress = Math.abs(deltaY) / effectiveFrameHeight;
                endHeight = deltaY < 0 ? (nextHeight || currentHeight) : (prevHeight || currentHeight);
            }
            progress = Math.max(0, Math.min(1, progress));

            if (typeof startHeight === 'number' && startHeight > 0 && typeof endHeight === 'number' && endHeight > 0) {
                const interpolatedHeight = startHeight + (endHeight - startHeight) * progress;
                wrapperEl.style.transition = ''; // Ensure no transition during interpolation
                wrapperEl.style.height = `${interpolatedHeight}px`;
            }
        }
        // --- End Height Interpolation ---

        // Move frames visually
        this.moveFramesBy(deltaX, deltaY);

        // Prevent page scroll if primarily dragging along the carousel axis
        const dragThreshold = 5;
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) > dragThreshold) {
            if (this.props.axis === "x" && Math.abs(deltaX) > Math.abs(deltaY) && e.cancelable) {
                e.preventDefault();
                e.stopPropagation();
            }
            if (this.props.axis === "y" && Math.abs(deltaY) > Math.abs(deltaX) && e.cancelable) {
                e.preventDefault();
                e.stopPropagation();
            }
        }
    }

    // Moves current, prev, next frames based on drag delta (instantly)
    moveFramesBy(deltaX, deltaY) {
        if (!this.state.movingFrames || !this.state.movingFrames.currentFrameIndex) return;
        const {prev: prevEl, currentFrameIndex: currentEl, next: nextEl} = this.state.movingFrames;
        const {frameWidth, frameHeight} = this.state;

        if (this.props.axis === 'x') {
            translateXY(currentEl, deltaX, 0, 0, 1);
            translateXY(nextEl, deltaX + frameWidth, 0, 0, deltaX < 0 ? 1 : 0); // Show if revealing
            translateXY(prevEl, deltaX - frameWidth, 0, 0, deltaX > 0 ? 1 : 0); // Show if revealing
        } else if (frameHeight) { // Y-axis
            translateXY(currentEl, 0, deltaY, 0, 1);
            translateXY(nextEl, 0, deltaY + frameHeight, 0, deltaY < 0 ? 1 : 0);
            translateXY(prevEl, 0, deltaY - frameHeight, 0, deltaY > 0 ? 1 : 0);
        }
    }


    onTouchEnd(e) {
        if (this.dragStartHeights === null) return; // Ignore if drag didn't start

        const direction = this.decideEndPosition(); // 'left', 'right', 'up', 'down', or 'origin'
        this.transitFramesTowards(direction); // Animate to final position

        this.dragStartHeights = null; // Reset cached heights
        this.setState({deltaX: 0, deltaY: 0, startX: 0, startY: 0}); // Reset drag state

        // Cleanup mouse listeners
        const wrapperEl = this.refs.wrapper;
        if (wrapperEl && (e.type === 'mouseup' || e.type === 'mouseleave')) {
            wrapperEl.removeEventListener("mousemove", this.onTouchMove, {capture: true});
            wrapperEl.removeEventListener("mouseup", this.onTouchEnd, {capture: true});
            wrapperEl.removeEventListener("mouseleave", this.onTouchEnd, {capture: true});
        }
        // Auto-slide restart is handled in transitFramesTowards
    }

    // Determines target direction ('left', 'right', 'up', 'down', 'origin') based on drag
    decideEndPosition() {
        const {deltaX = 0, deltaY = 0, currentFrameIndex, frames} = this.state;
        const {axis, loop, minMove} = this.props;

        if (!loop) { // Snap back if dragged past boundary (even slightly)
            const isFirst = currentFrameIndex === 0;
            const isLast = currentFrameIndex === frames.length - 1;
            if (axis === 'x') {
                if ((isFirst && deltaX > 0) || (isLast && deltaX < 0)) return "origin";
            } else {
                if ((isFirst && deltaY > 0) || (isLast && deltaY < 0)) return "origin";
            }
        }

        // Decide based on minimum move threshold
        switch (axis) {
            case "x":
                return Math.abs(deltaX) < minMove ? "origin" : (deltaX > 0 ? "right" : "left");
            case "y":
                return Math.abs(deltaY) < minMove ? "origin" : (deltaY > 0 ? "down" : "up");
            default:
                return "origin";
        }
    }

    // --- Frame Transition and Animation ---

    // Animates frames smoothly to the target position
    transitFramesTowards(direction) {
        if (!this.state.movingFrames || !this.state.movingFrames.currentFrameIndex) {
            this.prepareSiblingFrames(); // Attempt recovery
            if (!this.state.movingFrames || !this.state.movingFrames.currentFrameIndex) return; // Abort if still invalid
        }

        const {currentFrameIndex: currentEl, prev: prevEl, next: nextEl} = this.state.movingFrames;
        const {duration, axis, onTransitionEnd} = this.props;
        const {frameWidth, frameHeight} = this.state;

        let newCurrentFrameIndexId = this.state.currentFrameIndex;
        let targetEl = null; // The frame sliding *into* view
        switch (direction) {
            case "up":
            case "left":
                newCurrentFrameIndexId = this.getFrameId("next");
                targetEl = nextEl;
                break;
            case "down":
            case "right":
                newCurrentFrameIndexId = this.getFrameId("prev");
                targetEl = prevEl;
                break;
            case "origin":
                targetEl = currentEl;
                break; // Snapping back
            default:
                return; // Invalid direction
        }
        const indexActuallyChanged = newCurrentFrameIndexId !== this.state.currentFrameIndex;

        // Abort if target element is missing (can happen at boundaries in non-loop mode)
        if (!targetEl && (indexActuallyChanged || direction === 'origin')) {
            if (currentEl && direction !== 'origin') { // Snap current back if target missing
                translateXY(currentEl, 0, 0, duration, 1);
                if (axis === "x") {
                    translateXY(prevEl, -frameWidth, 0, duration, 0);
                    translateXY(nextEl, frameWidth, 0, duration, 0);
                } else if (axis === "y" && frameHeight) {
                    translateXY(prevEl, 0, -frameHeight, duration, 0);
                    translateXY(nextEl, 0, frameHeight, duration, 0);
                }
            }
            this.setState({deltaX: 0, deltaY: 0}, () => {
                this.prepareSiblingFrames();
                this.prepareAutoSlide();
            });
            return;
        }

        // Trigger smooth height animation to match the target frame
        this.updateWrapperHeight(newCurrentFrameIndexId, duration);

        // Ensure all frames *except* the outgoing (currentEl) and incoming (targetEl)
        // are instantly hidden before the transition starts.
        this.state.frames.forEach((_, i) => {
            const frameEl = this.refs[`f${i}`];
            if (frameEl && frameEl !== currentEl && frameEl !== targetEl) {
                translateXY(frameEl, 0, 0, 0, 0); // Hide instantly
            }
        });

        // Apply CSS transitions to animate frames
        switch (direction) {
            case "up":
                if (!frameHeight) break;
                translateXY(currentEl, 0, -frameHeight, duration, 0);
                translateXY(targetEl, 0, 0, duration, 1);
                break;
            case "down":
                if (!frameHeight) break;
                translateXY(currentEl, 0, frameHeight, duration, 0);
                translateXY(targetEl, 0, 0, duration, 1);
                break;
            case "left":
                translateXY(currentEl, -frameWidth, 0, duration, 0);
                translateXY(targetEl, 0, 0, duration, 1);
                break;
            case "right":
                translateXY(currentEl, frameWidth, 0, duration, 0);
                translateXY(targetEl, 0, 0, duration, 1);
                break;
            case "origin": // Snap back
                translateXY(currentEl, 0, 0, duration, 1);
                if (axis === "x") {
                    translateXY(prevEl, -frameWidth, 0, duration, 0);
                    translateXY(nextEl, frameWidth, 0, duration, 0);
                } else if (axis === "y" && frameHeight) {
                    translateXY(prevEl, 0, -frameHeight, duration, 0);
                    translateXY(nextEl, 0, frameHeight, duration, 0);
                }
                break;
        }

        // Define cleanup actions to run after transition
        const postTransitionCallback = () => {
            if (duration > 0) {
                setTimeout(() => {
                    if (!this.mounted) return;
                    this.prepareSiblingFrames(); // Reset positions for next interaction
                    if (onTransitionEnd) onTransitionEnd(this.state.currentFrameIndex);
                    this.prepareAutoSlide(); // Restart auto-slide timer
                }, duration);
            } else { // Instant transition
                this.prepareSiblingFrames();
                if (onTransitionEnd) onTransitionEnd(this.state.currentFrameIndex);
                this.prepareAutoSlide();
            }
        };

        // Update state index *then* run cleanup after state is set
        if (indexActuallyChanged) {
            this.setState({currentFrameIndex: newCurrentFrameIndexId}, postTransitionCallback);
        } else {
            postTransitionCallback(); // Run cleanup even if index didn't change
        }
    }


    transitToIndex(index, duration) {
        if (!this.mounted || index === this.state.currentFrameIndex) return;

        this.clearAutoTimeout(); // Stop auto-slide

        const { currentFrameIndex: currentIndex } = this.state;
        const { axis, onTransitionEnd } = this.props;
        const { frameWidth, frameHeight } = this.state;

        // Get the elements
        const currentEl = this.refs[`f${currentIndex}`];
        const targetEl = this.refs[`f${index}`];

        if (!currentEl || !targetEl) {
            console.warn("[SVCarousel] transitToIndex failed, falling back to instant setFrame.");
            // Fallback to instant change
            this.setFrame(index, 0);
            return;
        }

        // Determine direction and positions
        const direction = index > currentIndex ? 'left' : 'right'; // For 'x' axis
        const v_direction = index > currentIndex ? 'up' : 'down';   // For 'y' axis

        // 1. Instantly position the target frame off-screen *before* animation
        if (axis === 'x') {
            const startX = direction === 'left' ? frameWidth : -frameWidth;
            translateXY(targetEl, startX, 0, 0, 0); // Position off-screen, hidden
        } else if (axis === 'y' && frameHeight) {
            const startY = v_direction === 'up' ? frameHeight : -frameHeight;
            translateXY(targetEl, 0, startY, 0, 0); // Position off-screen, hidden
        } else {
            // Fallback for y-axis if height is 0, just fade
            translateXY(targetEl, 0, 0, 0, 0);
        }

        // A slight delay to ensure browser applies start position before transition
        // This is a common hack to force a style reflow
        setTimeout(() => {
            if (!this.mounted) return;

            // 2. Animate wrapper height
            this.updateWrapperHeight(index, duration);

            // 3. Animate frames
            if (axis === 'x') {
                const endX = direction === 'left' ? -frameWidth : frameWidth;
                translateXY(currentEl, endX, 0, duration, 0); // Slide current off-screen
                translateXY(targetEl, 0, 0, duration, 1);    // Slide target to center
            } else if (axis === 'y' && frameHeight) {
                const endY = v_direction === 'up' ? -frameHeight : frameHeight;
                translateXY(currentEl, 0, endY, duration, 0); // Slide current off-screen
                translateXY(targetEl, 0, 0, duration, 1);    // Slide target to center
            } else {
                // Fallback: Just cross-fade
                translateXY(currentEl, 0, 0, duration, 0);
                translateXY(targetEl, 0, 0, duration, 1);
            }

            // 4. Set state and run post-transition cleanup
            const postTransitionCallback = () => {
                if (duration > 0) {
                    setTimeout(() => {
                        if (!this.mounted) return;
                        this.prepareSiblingFrames(); // Reset positions for next drag
                        if (onTransitionEnd) onTransitionEnd(this.state.currentFrameIndex);
                        this.prepareAutoSlide(); // Restart auto-slide timer
                    }, duration);
                } else { // Instant
                    this.prepareSiblingFrames();
                    if (onTransitionEnd) onTransitionEnd(this.state.currentFrameIndex);
                    this.prepareAutoSlide();
                }
            };

            this.setState({ currentFrameIndex: index }, postTransitionCallback);

        }, 16); // ~1 frame delay to ensure start position is set
    }


    // --- Auto Sliding ---

    clearAutoTimeout() {
        clearTimeout(this.state.slider);
        if (this.state.slider) this.setState({slider: null});
    }

    prepareAutoSlide() {
        if (this.state.frames.length < 2 || !this.props.auto || !this.props.loop) return;
        this.clearAutoTimeout();
        if (this.mounted) {
            const slideTimeoutID = setTimeout(this.autoSlide, this.props.interval);
            this.setState({slider: slideTimeoutID});
        }
    }

    autoSlide(rel = "next") {
        this.clearAutoTimeout();
        const direction = this.props.axis === "x" ? (rel === "prev" ? "right" : "left") : (rel === "prev" ? "down" : "up");
        this.transitFramesTowards(direction);
    }

    // --- Public Methods / Navigation ---

    next() {
        if (!this.props.loop && this.state.currentFrameIndex === this.state.frames.length - 1) return false;
        this.autoSlide("next");
        return true;
    }

    prev() {
        if (!this.props.loop && this.state.currentFrameIndex === 0) return false;
        this.autoSlide("prev");
        return true;
    }

    // Set the current frame directly by index, optionally animating
    setFrame(index, ms = undefined) {
        index = parseInt(index, 10);
        if (isNaN(index) || index < 0 || index >= this.state.frames.length || index === this.state.currentFrameIndex) return;

        this.clearAutoTimeout();

        if (ms === undefined || ms <= 0) { // Instant change
            this.setState({currentFrameIndex: index}, () => {
                this.prepareSiblingFrames(true, index);
                this.updateWrapperHeight(index, 0);
                this.prepareAutoSlide();
            });
        } else {
            this.transitToIndex(index, ms);
        }
    }

    // Method exposed via props.setClick
    changeFrame() {
        if (this.props.desiredSlide !== undefined && this.props.desiredSlide !== this.state.currentFrameIndex) {
            this.setFrame(this.props.desiredSlide, this.props.duration);
        }
    }

    // --- Resize Handling ---

    onResize() { // Debounced resize handler
        clearTimeout(this.debounceTimeoutId);
        this.debounceTimeoutId = setTimeout(() => {
            if (!this.mounted) return;
            this.updateFrameSize(() => {
                this.prepareSiblingFrames(); // Reposition based on new size
                this.updateWrapperHeight(this.state.currentFrameIndex, 0); // Update height
            });
        }, 50);
    }

    updateFrameSize(cb) { // Update state based on wrapper dimensions
        const wrapperEl = this.refs.wrapper;
        if (wrapperEl) {
            const offsetWidth = wrapperEl.offsetWidth;
            const offsetHeight = wrapperEl.offsetHeight; // Use offsetHeight for Y-axis calculations
            if (!isNaN(offsetWidth) && offsetWidth > 0) {
                this.setState({frameWidth: offsetWidth, frameHeight: offsetHeight}, cb);
            } else if (cb) cb(); // Still call callback if update fails
        } else if (cb) cb();
    }

    render() {
        const {frames, currentFrameIndex, frameHeight} = this.state;
        const {widgets, axis, loop, auto, interval} = this.props;
        const outerWrapperStyle = {height: "100%", ...this.props.style};
        const innerWrapperStyle = {
            ...styles.wrapper,
            height: (axis === 'x' && frameHeight > 0) ? frameHeight : styles.wrapper.height
        };

        return (
            <div style={outerWrapperStyle} className={this.props.className || ''}>
                <div ref="wrapper" style={innerWrapperStyle}>
                    {/* Render frames from state */}
                    {frames.map((frameContent, i) => {
                        const frameStyle = {...styles.frame, zIndex: 99 - Math.abs(currentFrameIndex - i), opacity: 0}; // Default hidden
                        return (
                            <div ref={"f" + i} key={i} style={frameStyle}>
                                {/* Inner wrapper for height calculation */}
                                <div style={styles.innerFrameWrapper} data-frame-content-wrapper="true">
                                    {frameContent}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {widgets && [].concat(widgets).map((Widget, i) => (
                    <Widget key={i} index={currentFrameIndex} total={frames.length} prevHandler={this.prev}
                            nextHandler={this.next} setFrameHandler={this.setFrame} axis={axis} loop={loop} auto={auto}
                            interval={interval}/>
                ))}
            </div>
        );
    }
}


SVCarousel.propTypes = {
    axis: PropTypes.oneOf(["x", "y"]), auto: PropTypes.bool, loop: PropTypes.bool,
    interval: PropTypes.number, duration: PropTypes.number, minMove: PropTypes.number,
    onStartFrameIndex: PropTypes.number, frames: PropTypes.arrayOf(PropTypes.element),
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
    widgets: PropTypes.arrayOf(PropTypes.func), style: PropTypes.object, className: PropTypes.string,
    onTransitionEnd: PropTypes.func, setClick: PropTypes.func,
    currentFrameIndex: PropTypes.number, // External control prop (use carefully)
    desiredSlide: PropTypes.number, // External control prop
};

SVCarousel.defaultProps = {
    axis: "x", auto: false, loop: false, interval: 5000, duration: 300,
    minMove: 42, style: {}, className: '', onStartFrameIndex: 0,
};

export default SVCarousel;
